<script setup lang="ts">
import { computed, ref } from 'vue'
import { revokeUserDiscountAction } from '@/api/functions/discounts/revoke-user-discount-action'
import { useMutationApi } from '@/composables/useApi'
import { useJalaaliDate } from '@/composables/useJalaaliDate'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'
import { numberWithCommas } from '@/utils/number-with-commas'

export interface RevokeUserDiscountResponse {
  data: null
  meta: {
    message: string
    errors: string[]
  }
}

const props = defineProps({
  discount: {
    type: Object,
    required: true,
  },
  userId: {
    type: String,
    required: true,
  },
})

const emit = defineEmits(['confirmDiscount'])

const { toJalaali } = useJalaaliDate()
const toast = useToast()
const showRevokeModal = ref(false)

const formatDate = (dateString: string) => {
  if (!dateString) return ''
  const jDate = toJalaali(dateString)
  return `${jDate} `
}

const revokeMutation = useMutationApi({
  mutationFn: () => revokeUserDiscountAction(props.userId, props.discount.id, props.discount.discount_user.id),
  onSuccess: (response) => {
    toast.success(response?.meta?.message || 'تخفیف با موفقیت از کاربر حذف شد')
    emit('confirmDiscount')
    showRevokeModal.value = false
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleRevokeDiscount = () => {
  if (!props.discount?.discount_user?.id) return

  revokeMutation.mutate()
}
</script>

<template>
    <div class="bg-white rounded-xl p-5 lg:p-8 lg:pb-6 relative flex flex-col-reverse lg:flex-col gap-6 lg:gap-0">

        <ConfirmModal v-model="showRevokeModal" title="حذف تخصیص تخفیف"
            message="آیا از حذف تخصیص این تخفیف از کاربر اطمینان دارید؟" confirm-text="بله، حذف شود"
            cancel-text="بازگشت" @confirm="handleRevokeDiscount" @cancel="showRevokeModal = false" />


        <div v-if="discount?.status_type === 'unUsed'" class="relative lg:absolute bottom-0 lg:top-8 lg:end-8">
            <div class="w-full lg:w-fit flex items-center">
                <Button @click="showRevokeModal = true" :isAdmin="true" variant="outline"
                    class="w-full text-nowrap lg:w-auto border-2 rounded-[10px] px-4! h-10! py-0 text-[13px] me-2">
                    حذف تخصیص
                </Button>
            </div>
        </div>

        <div class="flex flex-col gap-6  text-black text-sm font-medium">
            <!-- Header -->
            <div class="w-full lg:w-[45%] flex items-center justify-center gap-2 lg:justify-between">
                <div
                    class="w-full lg:w-fit bg-[#eaeef5] text-[#2d5396] flex items-center justify-center text-[13px] font-medium h-10 rounded-[10px] px-6">
                    شماره تخفیف
                    <span class="font-fa ms-1">{{ discount?.id }}</span>
                    <span class="mx-1">|</span>
                    <span :class="discount?.status_type === 'unUsed' ? 'text-[#008000]' :
                        discount?.status_type === 'inUse' ? 'text-[#a52a2a]' :
                            discount?.status_type === 'used' ? 'text-[#ff0000]' :
                                discount?.status_type === 'isExpired' ? 'text-[#ff0000]' :
                                    ''
                        ">
                        {{ discount?.status_type === 'unUsed' ? 'استفاده نشده' :
                            discount?.status_type === 'inUse' ? 'در حال استفاده' :
                                discount?.status_type === 'used' ? 'استفاده شده در تاریخ' :
                                    discount?.status_type === 'isExpired' ? 'منقضی شده' :
                                        '' }}
                    </span>
                    <span v-if="discount?.status_type === 'used'" class="ms-1 font-fa text-[#0000ff]">{{
                        formatDate(discount?.used_at)
                        }}</span>
                </div>
            </div>
            <!-- Body -->
            <div class="flex flex-col gap-2.5 text-sm text-black font-medium">
                <div class="flex items-center gap-1">
                    <span>{{ discount?.title }}</span>
                </div>
                <div class="flex items-center gap-1">
                    <span>نوع تخفیف:</span>
                    <span>{{ discount?.type?.titleFa }}</span>
                </div>
                <div class="flex items-center gap-1">
                    <span>مقدار تخفیف(%):</span>
                    <span class="font-fa">{{ discount?.rate }}</span>
                </div>
                <div class="flex items-center gap-1">
                    <span>سقف حجمی تخفیف:</span>
                    <span class="font-fa">{{ numberWithCommas(discount?.volume_limit) }}</span>
                </div>
                <div class="flex items-center gap-1">
                    <span>نوع اعتبار:</span>
                    <span>{{
                        discount?.credit_type === 'ONE_TIME_USE' ? ' یک‌بار مصرف' : '' }}</span>
                </div>
                <div v-if="discount?.transaction" class="flex items-center gap-1">
                    <span>کد تراکنش:</span>
                    <span>
                        {{ discount?.transaction?.code }}
                    </span>
                </div>
                <div class="flex items-center gap-1">
                    <span>نوع ارز:</span>
                    <span>{{ discount?.currency?.length ? 'همه ارزها' : discount?.currency?.titleFa }}</span>
                </div>

            </div>
        </div>
    </div>

</template>
